import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { ApiConnectionString } from "../../../constants/ApiConntection";

export default function Availability({ profile ,user }) {
  const days = useMemo(
    () => [
      { key: "monday", label: "Mon" },
      { key: "tuesday", label: "Tue" },
      { key: "wednesday", label: "Wed" },
      { key: "thursday", label: "Thu" },
      { key: "friday", label: "Fri" },
      { key: "saturday", label: "Sat" },
      { key: "sunday", label: "Sun" },
    ],
    []
  );

  const blocks = useMemo(
    () => [
      { key: "morning", label: "Morning" },
      { key: "afternoon", label: "Afternoon" },
      { key: "evening", label: "Evening" },
    ],
    []
  );

  const empty = useMemo(
    () => ({
      morning: [0, 0, 0, 0, 0, 0, 0],
      afternoon: [0, 0, 0, 0, 0, 0, 0],
      evening: [0, 0, 0, 0, 0, 0, 0],
    }),
    []
  );

  const buildAvailabilityFromProfile = (p) => {
    const safe = p || {};
    const next = {};

    blocks.forEach((b) => {
      next[b.key] = days.map((d) => {
        const propKey =
          d.key + b.key.charAt(0).toUpperCase() + b.key.slice(1);
        return safe[propKey] ? 1 : 0;
      });
    });

    return next;
  };

  const initial = useMemo(() => {
    if (!profile) return empty;
    return buildAvailabilityFromProfile(profile);
  }, [profile, empty]);

  const [availability, setAvailability] = useState(initial);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setAvailability(initial);
    setIsEditing(false);
  }, [initial]);

  const getRow = (key) => availability[key] || [0, 0, 0, 0, 0, 0, 0];

  const toggle = (blockKey, dayIndex) => {
    if (!isEditing) return;

    setAvailability((prev) => {
      const next = { ...prev };
      const row = [...(next[blockKey] || [0, 0, 0, 0, 0, 0, 0])];
      row[dayIndex] = row[dayIndex] ? 0 : 1;
      next[blockKey] = row;
      return next;
    });
  };

  const toProfileShape = (grid, base = {}) => {
    const out = { ...base };

    blocks.forEach((b) => {
      days.forEach((d, dayIndex) => {
        const propKey =
          d.key + b.key.charAt(0).toUpperCase() + b.key.slice(1);
        out[propKey] = (grid[b.key] || [0, 0, 0, 0, 0, 0])[dayIndex] === 1;
      });
    });

    return out;
  };

  const name =
    (profile && (profile.name || profile.fullName || profile.displayName)) ||
    "Your profile";

  const totals = useMemo(() => {
    let on = 0;
    let off = 0;

    blocks.forEach((b) => {
      getRow(b.key).forEach((v) => {
        if (v) on += 1;
        else off += 1;
      });
    });

    return { on, off };
  }, [availability, blocks]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setAvailability(initial);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const payload = toProfileShape(availability, profile || {});
   const res = await axios.put(`${ApiConnectionString}/api/counsellorProfile`,payload,
      {
        headers: { Authorization: `bearer ${user.token}` },
      } )

    setIsEditing(false);
  };

  return (
    <div className="db-card">
      <div className="db-main-head d-flex flex-column gap-3">
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div className="flex-grow-1">
            <h1 className="db-h1 mb-1">Availability</h1>
            <div className="db-sub">
              {isEditing
                ? `Edit availability for ${name}`
                : `Choose when clients can request sessions for ${name}`}
            </div>
          </div>

          {!isEditing ? (
            <button
              type="button"
              className="db-btn db-btn-primary"
              onClick={handleEdit}
            >
              Edit
            </button>
          ) : (
            <div className="d-flex gap-2">
              <button
                type="button"
                className="db-btn db-btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>

              <button
                type="button"
                className="db-btn db-btn-primary"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          )}
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <span className="db-badge db-badge-green">{totals.on} available</span>
          <span className="db-badge db-badge-blue">{totals.off} unavailable</span>
        </div>
      </div>

      <div className="db-divider" />

      <div className="db-av-card">
        <div className="d-block d-sm-none">
          <div className="d-flex flex-column gap-2">
            {days.map((d, dayIndex) => (
              <div key={d.key} className="db-av-day-card">
                <div className="db-av-day-card-head">
                  <div className="db-av-day-card-title">{d.label}</div>
                  {!isEditing ? (
                    <span className="db-badge db-badge-blue">View</span>
                  ) : (
                    <span className="db-badge db-badge-green">Editing</span>
                  )}
                </div>

                <div className="db-av-day-card-body">
                  {blocks.map((b) => {
                    const on = getRow(b.key)[dayIndex] === 1;

                    return (
                      <button
                        key={`${d.key}-${b.key}`}
                        type="button"
                        className={
                          on
                            ? "db-av-row-btn db-av-row-btn-on"
                            : "db-av-row-btn db-av-row-btn-off"
                        }
                        onClick={() => toggle(b.key, dayIndex)}
                        aria-pressed={on}
                        aria-label={`${d.label} ${b.label} ${
                          on ? "available" : "unavailable"
                        }`}
                        disabled={!isEditing}
                      >
                        <span className="db-av-row-btn-label">{b.label}</span>
                        <span className="db-av-row-btn-state">
                          {on ? "Available" : "Unavailable"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-none d-sm-block">
          <div className="db-av-scroll">
            <div className="db-av-grid">
              <div className="db-av-corner"></div>

              {days.map((d) => (
                <div key={d.key} className="db-av-day">
                  {d.label}
                </div>
              ))}

              {blocks.map((b) => (
                <React.Fragment key={b.key}>
                  <div className="db-av-label">{b.label}</div>

                  {days.map((_, dayIndex) => {
                    const on = getRow(b.key)[dayIndex] === 1;

                    return (
                      <button
                        key={`${b.key}-${dayIndex}`}
                        type="button"
                        className={
                          on
                            ? "db-av-cell db-av-cell-on"
                            : "db-av-cell db-av-cell-off"
                        }
                        onClick={() => toggle(b.key, dayIndex)}
                        aria-pressed={on}
                        aria-label={`${b.label} ${days[dayIndex].label} ${
                          on ? "available" : "unavailable"
                        }`}
                        disabled={!isEditing}
                        title={isEditing ? "Click to toggle" : "Click Edit to change"}
                      >
                        {on ? "✓" : ""}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="db-divider" />

        <div className="d-flex flex-column gap-2">
          <div className="db-soft-note">
            {isEditing
              ? "Tap cells to toggle availability, then press Save"
              : "Clients see these time blocks when they send an enquiry or request a booking"}
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <span className="db-badge db-badge-green">Available</span>
            <span className="db-badge db-badge-blue">Unavailable</span>
          </div>
        </div>
      </div>
    </div>
  );
}
